import { DOCUMENT } from '@angular/common';
import { HttpClient, HttpContext, HttpHeaders } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';

import { BehaviorSubject, Observable, timer } from 'rxjs';
import { map } from 'rxjs/operators';

import { User, UserManager, UserManagerSettings } from 'oidc-client-ts';
import { IEnvironmentConfig, IOidc } from '../interfaces/config.interface';

import { Buffer } from 'buffer';
import { ApplicationId, AuthenticationState, ClientType, Constants, HttpContextTokens, ProviderType } from '../constants';
import { IApiResult } from '../interfaces/api-result.interface';
import {
  IAccountDetails,
  IApplicationProfile,
  IAuthCodeModel,
  ILoginCredentials,
  IMfaConfirmResponse,
  IMfaSetupResponse,
  INewAccountModel,
  IProfile,
  IRequestResetPasswordModel,
  IResetPasswordModel,
  IResetPasswordResponseModel,
  ISecurityEvent,
  IUserDetails,
  SecurityEvents,
} from '../interfaces/security.interfaces';
import { AccountContact } from '../models/account-contact.model';
import { AccountContactDTO } from '../models/dtos/account-contact.dto';
import { HttpLoggingOptions } from '../models/http-logging-options.model';
import { Router } from '@angular/router';
import { EmailLoginPermissionDTO } from '../models/dtos/email-login-permission.dto';

const decode = (str: string): string => Buffer.from(str, 'base64').toString('binary');
const encode = (str: string): string => Buffer.from(str, 'binary').toString('base64');

export enum CommonPermissions {
  CanModifyOnlineAccess = 1,
  CanModifyContacts = 2,
  CanModifyAccountDetails = 4,
}

export enum MarketDataPermissions {
  FixedBroadband = 8,
  LeasedLines = 16,
  NetworkMaps = 32,
  SpacialData = 64,
  Revenue = 128,
  Offers = 256,
  Investment = 512,
  Mobile = 1024,
  Complaints = 2048,
  PIA = 4096,
  MarketData = 5880,
  ScamMetrics = 8192,//Network Trust
  Odin = 16384
}

@Injectable()
export class SecurityService {
  private environment: IEnvironmentConfig;
  private oidc: IOidc;
  private manager: UserManager;
  private id: number;
  private clientType = 'DataPortal';
  private httpContext: HttpContext;
  private accountLoaded: BehaviorSubject<string> = new BehaviorSubject('');

  user: User = null;
  userEvents: BehaviorSubject<ISecurityEvent> = new BehaviorSubject(null);
  profile: IProfile = null;
  accountLoaded$: Observable<string> = this.accountLoaded.asObservable();
  applicationName: string = '';
  applicationProfile: IApplicationProfile = { applicationName: '', showSignUpButton: true, showTermsAndConditions: true };

  constructor(
    @Inject(DOCUMENT) private document: Document,
    private http: HttpClient,
    private router: Router
  ) {
    this.initialiseHttpContext();
    this.id = Math.random();
    console.log(`SECURITY SERVICE ID: ${this.id}`);
  }

  get hasAssociatedAccounts(): boolean {
    return this.isSuperUser || this.profile?.user?.accounts?.length > 0;
  }

  get authenticationState(): AuthenticationState {
    return this.user === null ? AuthenticationState.unauthorised : this.profile.state;
  }

  get accountIsVerified() {
    return this.user !== null && this.authenticationState === AuthenticationState.accountVerified;
  }

  set sourceApplication(value: string) {
    this.applicationName = value;
    this.applicationProfile.applicationName = value;
    this.userEvents.next({ eventType: SecurityEvents.sourceApplicationChanged, payload: value });
  }

  private get emptyProfile(): IProfile {
    return {
      username: '',
      accountCode: '',
      erauReference: '',
      state: AuthenticationState.unauthorised,
      selectedAccount: null,
      user: null,
    };
  }

  get apiClientType(): string {
    return this.clientType;
  }

  set apiClientType(clientType: string) {
    this.clientType = clientType;
  }

  get isSuperUser(): boolean {
    return this.profile.user?.isSuperUser ?? false;
  }

  set isSuperUser(isSuperUser: boolean) {
    if (isSuperUser) {
      this.profile.state = AuthenticationState.mfaRequired;
    }
    this.profile.user.isSuperUser = isSuperUser;
  }

  async initialise(config: IEnvironmentConfig): Promise<void> {
    this.loadProfile();

    this.environment = config;
    this.oidc = config.oidc;

    this.manager = new UserManager(this.mapSecuritySettings());

    this.manager.events.addUserLoaded((user: User) => {
      this.userEvents.next({ eventType: SecurityEvents.userLoaded, payload: user });
    });
    switch (this.manager.settings.extraQueryParams.source) {
      case ApplicationId.eLicensing:
        this.sourceApplication = 'eLicensing';
        break;
      case ApplicationId.DataPortal:
        this.sourceApplication = 'data.comreg.ie';
        break;
      case ApplicationId.SenderId:
        this.sourceApplication = 'Sender ID';
        this.applicationProfile.showSignUpButton = false;
        this.applicationProfile.showTermsAndConditions = false;
        break;
      default:
        this.sourceApplication = 'ComReg Login';
        break;
    }

    this.manager.events.addAccessTokenExpiring((_) => {
      const source = timer(10000);
      const subscribe = source.subscribe({
        complete: async () => {
          this.user = await this.manager.getUser();
        },
      });
    });

    this.manager.events.addAccessTokenExpired((_) => this.logout());

    return new Promise<void>(async (resolve) => {
      if (this.user === null) {
        this.manager.getUser().then((user) => {
          this.user = user;
          if (this.profile.state === AuthenticationState.accountVerified) {
            this.userEvents.next({ eventType: SecurityEvents.accountVerified, payload: this.user });
          }
          resolve();
        });
      }
    });
  }

  async login(credentials: ILoginCredentials) {
    credentials.clientType = this.clientType;
    const url = `${this.oidc.stsServer}/authenticate/login`;
    return this.http
      .post(url, credentials, this.getOptions(true))
      .toPromise()
      .then(
        (res) => {
          this.profile.state = AuthenticationState.credentials;
          this.profile.username = credentials.username;
          this.saveProfile();
          this.document.location.href = (res as any).redirectUrl;
        },
        (err) => {
          this.userEvents.next({ eventType: SecurityEvents.badCredentials, payload: err });
        }
      );
  }

  logout() {
    this.userEvents.next({ eventType: SecurityEvents.userSignedOut, payload: null });
    this.manager.signoutRedirect();
  }

  completeLogout() {
    this.user = null;
    this.profile = this.emptyProfile;
    localStorage.removeItem('profile');
    this.manager.clearStaleState();
    return this.manager.signoutRedirectCallback();
  }

  isLoggedIn(): boolean {
    return this.user != null && !this.user.expired;
  }

  getClaims(): any {
    return this.user.profile;
  }

  hasClaim(key: string, value: string): boolean {
    const claims = this.getClaims();
    return claims[key] === value ? true : false;
  }

  getClaim(key: string): any {
    const claims = this.getClaims();
    return claims[key];
  }

  getAuthorizationHeaderValue(): string {
    return `${this.user.token_type} ${this.user.access_token}`;
  }

  startAuthentication(): Promise<void> {
    return this.manager.signinRedirect();
  }

  completeAuthentication(): Promise<void> {
    return this.manager.signinRedirectCallback().then(async (user) => {
      this.userEvents.next({ eventType: SecurityEvents.userSignedIn, payload: user });
      this.user = user;
      await this.populateProfile();
    });
  }

  async populateProfile() {
    //As the login component is now hosted in a different application, we need to make sure the profile is populated correctly
    //We can use the claims held against the user (from identity server) to fill in any gaps
    if (!this.profile.username) this.profile.username = this.user.profile[Constants.claimTypes.userEmail] as string;
    if (!this.profile.erauReference) this.profile.erauReference = this.user.profile[Constants.claimTypes.erauReference] as string;

    const url: string = `${this.environment.gatewayUrl}/users/current`;
    await this.http
      .get<IUserDetails>(url)
      .toPromise()
      .then((response) => {
        //Filter out accounts that do not apply to this application i.e. MarketData (4) and NOU (5) for eLicensing and vice versa also all Numbering (3)
        if (this.environment.applicationId == ApplicationId.eLicensing)
          response.accounts = response.accounts.filter(
            (account: IAccountDetails) =>
              account.providerType === ProviderType.PRS ||
              account.providerType === ProviderType.RADIO ||
              account.providerType === ProviderType.BROADCASTING
          );
        else if (this.environment.applicationId === ApplicationId.DataPortal) {
          response.accounts = response.accounts.filter(
            (account: IAccountDetails) => account.providerType === ProviderType.MARKETDATA || account.providerType === ProviderType.NETWORKOPERATIONS
          );
        } else if (this.environment.applicationId === ApplicationId.SenderId) {
          response.accounts = response.accounts.filter(
            (account: IAccountDetails) => account.providerType === ProviderType.SENDERID
          );
        }

        this.profile.user = response;

        if (!this.profile.user?.name) {
          this.profile.user.name = `${this.user.profile[Constants.claimTypes.firstName] ?? ''
            } ${this.user.profile[Constants.claimTypes.lastName] ?? ''}`.trim();
        }
        this.profile.state = AuthenticationState.mfaRequired;
        this.saveProfile();
      });
  }

  async requestMfaDetails(): Promise<IMfaSetupResponse> {
    const url: string = `${this.environment.gatewayUrl}/users/${encodeURIComponent(this.profile.username)}/mfa`;
    return await this.http.get<IMfaSetupResponse>(url, { context: this.httpContext }).toPromise();
  }

  async confirmMfaCode(authCode: string): Promise<IMfaConfirmResponse> {
    const url: string = `${this.environment.gatewayUrl}/users/${encodeURIComponent(this.profile.username)}/mfa/confirm?mfaToken=${authCode}`;
    return await this.http.post<IMfaConfirmResponse>(url, null, { context: this.httpContext }).toPromise();
  }

  mfaCompleted(): void {
    let user: IUserDetails = this.profile.user;
    if (!user.isSuperUser) {
      if (user.accounts) {
        if (user.accounts.length === 0) {
          this.profile.state = AuthenticationState.noAccounts;
        } else {
          this.profile.state = AuthenticationState.selectAccount;
        }
      } else {
        this.profile.state = AuthenticationState.noAccounts;
      }
    } else {
      this.profile.state = AuthenticationState.selectAccountCode;
    }
    this.saveProfile();
  }

  redirectToHome(): string {
    var route: string = '';
    if (this.accountIsVerified && !this.hasAssociatedAccounts) {
      route = 'profile';
    } else {
      switch (this.profile.selectedAccount.providerType) {
        case ProviderType.PRS:
          route = 'prs';
          break;
        case ProviderType.RADIO:
          route = 'radio';
          break;
        case ProviderType.BROADCASTING:
          route = 'broadcasting';
          break;
        case ProviderType.MARKETDATA:
          route = 'market-data';
          break;
        case ProviderType.NETWORKOPERATIONS:
          route = 'network-operations';
          break;
        case ProviderType.SENDERID:
          route = '';
          break;
      }
    }
    return `/${route}`;
  }

  requestAuthorisationCode(contactId: number): Promise<boolean> {
    const authcodeModel: IAuthCodeModel = {
      selectedContactId: contactId,
      regenerateCode: true,
      timeoutHours: 12,
      clientType: this.clientType,
    };

    return new Promise<boolean>((resolve, reject) => {
      this.http
        .put<IApiResult<void>>(`${this.environment.gatewayUrl}/multifactor/:accountCode/authcode/generate`, authcodeModel, this.getOptions(false))
        .subscribe({
          complete: () => {
            this.profile.selectedAccount.contactId = contactId;
            this.profile.state = AuthenticationState.authCodeRequested;
            this.saveProfile();
            resolve(true);
          },
          error: () => reject(false),
        });
    });
  }

  markAccountAsVerified(): void {
    this.profile.state = AuthenticationState.accountVerified;
    this.saveProfile();
    this.userEvents.next({ eventType: SecurityEvents.accountVerified, payload: this.user });
  }

  switchSelectedAccount() {
    let user: IUserDetails = this.profile.user;
    if (user.isSuperUser) {
      this.profile.state = AuthenticationState.selectAccountCode;
    } else {
      this.profile.state = AuthenticationState.selectAccount;
    }
    this.saveProfile();
    this.userEvents.next({ eventType: SecurityEvents.accountVerified, payload: this.user });
  }

  getSelectedAccount() {
    return this.profile.selectedAccount;
  }

  async validateAccountCode(accountCode: string): Promise<boolean> {
    //this needs to be quite different - if just needs to check if the account exists and if so do the same as if the user logged in and selected that account
    const url = `${this.environment.gatewayUrl}/v2/accounts/${accountCode}`;
    return this.http
      .get<IApiResult<IAccountDetails>>(url, this.getOptions(false))
      .toPromise()
      .then((result) => {
        if (result !== null) {
          this.manager.storeUser(this.user);

          //the account is valid so for superusers just set it as the selected account,
          //for standard users, ensure it exists in their "accounts" list and set that as the selected account.
          if (this.profile.user.isSuperUser) {
            this.profile.selectedAccount = result.value;
          } else {
            this.profile.selectedAccount = this.profile.user.accounts.filter((a) => a.accountCode == result.value.accountCode.trim())[0];
          }
          if (this.profile.selectedAccount?.accountCode) {
            this.profile.selectedAccount.accountCode = this.profile.selectedAccount.accountCode.trim();
          }
          // raise an event to let all existing services know to reset themselvs as state whould be reset between account switches
          this.accountLoaded.next(this.profile.selectedAccount.accountCode);
          this.saveProfile();

          return true;
        } else {
          return false;
        }
      })
      .catch(() => false);
  }

  saveProfile() {
    localStorage.setItem('profile', encode(JSON.stringify(this.profile)));
  }

  loadProfile() {
    var rawProfile: string = localStorage.getItem('profile');
    if (rawProfile) {
      this.profile = JSON.parse(decode(rawProfile));
    }
    if (this.profile === null) {
      this.profile = this.emptyProfile;
    }
  }

  private async getEmailLoginPermission(): Promise<EmailLoginPermissionDTO> {
    var url = `${this.environment.gatewayUrl}/v2/Accounts/:accountCode/emailLoginPermission/${this.profile.username}`;
    return await this.http
      .get<IApiResult<EmailLoginPermissionDTO>>(url)
      .pipe(map(response => response.value)).toPromise();
  }

  async getContacts(isSuperUser: boolean, includeContactsWithNoEmail: boolean = true, declarationStep: boolean = false): Promise<AccountContact[]> {
    var url = `${this.environment.gatewayUrl}/v2/Accounts/:accountCode/contacts?includeContactsWithNoEmail=${includeContactsWithNoEmail}`;
    return await this.http
      .get<IApiResult<AccountContactDTO[]>>(url)
      .pipe(
        map(async (response) => {
          if (isSuperUser === true && this.profile.selectedAccount?.providerType == ProviderType.MARKETDATA && !declarationStep) {
            const contactPermission = parseInt((await this.getEmailLoginPermission())?.permission);
            if (contactPermission !== MarketDataPermissions.Odin) response.value = [];
            const acc: (AccountContact[] | AccountContact) = this.createContacts(contactPermission);
            response.value.unshift(...acc);
          }
          return [...new Map(response.value.map(i => [i.lcAuto, AccountContact.fromDto(i)])).values()];
        })
      )
      .toPromise();
  }

  private createContacts(emailLoginPermission: number): AccountContact[] {
    var acc: AccountContact[] = [];
    if (emailLoginPermission === MarketDataPermissions.Complaints || emailLoginPermission === MarketDataPermissions.Odin) {
      acc.push(new AccountContact(AuthenticationState.selectAccountCode, 136313, '', 'Provider', 'Complaints', '', false, false, false, '', '', '', '', '', '', '', '', '', '', 'complaintsdata@comreg.ie', false, false, -1, 3));
    }
    if (emailLoginPermission === MarketDataPermissions.MarketData || emailLoginPermission === MarketDataPermissions.Odin) {
      acc.push(new AccountContact(AuthenticationState.selectAccountCode, 126817, '', 'Market', 'Data', '', false, false, false, '', '', '', '', '', '', '', '', '', '', 'marketdata@comreg.ie', false, false, -1, 1));
    }
    if (emailLoginPermission === MarketDataPermissions.Offers || emailLoginPermission === MarketDataPermissions.Odin) {
      acc.push(new AccountContact(AuthenticationState.selectAccountCode, 135823, '', 'Retail', 'Offers', '', false, false, false, '', '', '', '', '', '', '', '', '', '', 'comparefeedback@comreg.ie', false, false, -1, 2));
    }
    if (emailLoginPermission === MarketDataPermissions.ScamMetrics || emailLoginPermission === MarketDataPermissions.Odin) {
      acc.push(new AccountContact(AuthenticationState.selectAccountCode, 136314, '', 'Network', 'Trust', '', false, false, false, '', '', '', '', '', '', '', '', '', '', 'metrics.reporting@comreg.ie', false, false, -1, 4));
    }
    return acc;
  }

  requestPasswordReset(email: string): Observable<IApiResult<boolean>> {
    var clientType: string;

    switch (this.environment.applicationId) {
      case ApplicationId.eLicensing:
        clientType = ClientType.eLicensing;
        break;
      case ApplicationId.DataPortal:
        clientType = ClientType.DataPortal;
        break;
      case ApplicationId.SenderId:
        clientType = ClientType.SenderId;
        break;
      default:
        clientType = null;
        break;
    }

    const requestPin: IRequestResetPasswordModel = {
      email,
      clientType: clientType,
    };

    return this.http.put<IApiResult<boolean>>(
      `${this.environment.gatewayUrl}/users/${encodeURIComponent(email)}/password/request`,
      requestPin,
      this.getOptions(false)
    );
  }

  requestMfaReset(email: string): Observable<IApiResult<boolean>> {
    return this.http.get<IApiResult<boolean>>(
      `${this.environment.gatewayUrl}/users/${encodeURIComponent(email)}/mfa?disable=true`,
      this.getOptions(false)
    );
  }

  resetPassword(username: string, token: string, newPassword: string): Observable<IApiResult<object>> {
    const resetPasswordModel: IResetPasswordModel = {
      token,
      newPassword,
    };

    return this.http.put<IApiResult<IResetPasswordResponseModel>>(
      `${this.environment.gatewayUrl}/users/${encodeURIComponent(username)}/password/reset`,
      resetPasswordModel,
      this.getOptions(false)
    );
  }

  requestNewLogin(newAccount: INewAccountModel): Observable<IApiResult<object>> {
    return this.http.post<IApiResult<object>>(
      `${this.environment.gatewayUrl}/users/${encodeURIComponent(newAccount.email)}`,
      newAccount,
      this.getOptions(false)
    );
  }

  private getOptions(withCredentials: boolean) {
    return {
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
      }),
      withCredentials,
      context: this.httpContext,
    };
  }

  private mapSecuritySettings(): UserManagerSettings {
    const stsServer: string = this.oidc.stsServer;
    const applicationUrl: string = this.oidc.applicationUrl;
    const userManagerSettings: UserManagerSettings = this.oidc.settings;

    const post_logout_redirect_uri: string = `${applicationUrl}/${userManagerSettings.post_logout_redirect_uri}`;

    return {
      authority: stsServer,
      client_id: userManagerSettings.client_id,
      redirect_uri: `${applicationUrl}/${userManagerSettings.redirect_uri}`,
      post_logout_redirect_uri: post_logout_redirect_uri,
      revokeTokensOnSignout: userManagerSettings.revokeTokensOnSignout,
      response_type: userManagerSettings.response_type,
      scope: userManagerSettings.scope,
      filterProtocolClaims: true,
      loadUserInfo: true,
      automaticSilentRenew: userManagerSettings.automaticSilentRenew,
      silent_redirect_uri: userManagerSettings.silent_redirect_uri,
      extraQueryParams: { source: this.environment.applicationId, postLogoutRedirectUri: post_logout_redirect_uri }, //we include the post logout uri here too as, in login failure scenarios it is not passed correctly on logout
    };
  }

  private initialiseHttpContext() {
    this.httpContext = new HttpContext();

    var httpLoggingOptions = new HttpLoggingOptions();

    //Because the calls here can include sensitive info then we should not log them
    httpLoggingOptions.doNotLog = true;

    this.httpContext.set(HttpContextTokens.options, httpLoggingOptions);
  }

  hasPermission(permission: number): boolean {
    if (this.profile?.selectedAccount?.providerType === ProviderType.MARKETDATA) {
      return (this.profile.selectedAccount.accountPermissions & permission) === permission;
    } else {
      return true;
    }
  }

  get maxPermisisons(): number {
    if (this.profile.selectedAccount?.providerType === ProviderType.MARKETDATA) {
      return this.sumEnum(CommonPermissions) + this.sumEnum(MarketDataPermissions);
    } else {
      return -1;
    }
  }

  private sumEnum(enumToSum): number {
    var sum = 0;
    const tmpArray = Object.keys(enumToSum);
    tmpArray.forEach((item, value) => {
      if (!isNaN(+item)) {
        sum += +item;
      }
    });
    return sum;
  }
}
